import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import './Users.css';

const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Users = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [showWaSubsModal, setShowWaSubsModal] = useState(false);
  const [waSubsUser, setWaSubsUser] = useState(null);
  const [waSubsRows, setWaSubsRows] = useState([]);
  const [waSubsSaving, setWaSubsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    status: 'A'
  });

  const usersPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm]);

  const fetchData = async () => {
    await Promise.all([fetchUsers(), fetchRoles()]);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers({
        page: currentPage,
        limit: usersPerPage,
        search: searchTerm
      });

      if (response.success && response.data) {
        setUsers(response.data.users || []);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages);
          setTotalUsers(response.data.pagination.total);
        }
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch users: ' + (err.message || 'Unknown error'));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await userService.getAllRoles();
      if (response.success && response.data) {
        setRoles(response.data.roles || []);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        const updateData = { ...formData, username: formData.phone };
        if (!updateData.password) {
          delete updateData.password;
        }
        await userService.updateUser(editingUser.id, updateData);
        setSuccess('User updated successfully');
      } else {
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        await userService.createUser({ ...formData, username: formData.phone });
        setSuccess('User created successfully');
      }

      fetchUsers();
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      status: user.status || 'A'
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        setSuccess('User deleted successfully');
        fetchUsers();
      } catch (err) {
        setError('Failed to delete user: ' + (err.message || 'Unknown error'));
      }
    }
  };

  const handleManageRoles = async (userToManage) => {
    try {
      setLoading(true);
      const userDetailsRes = await userService.getUserById(userToManage.id);

      if (userDetailsRes.success) {
        const userWithRoles = userDetailsRes.data.user;
        setSelectedUser(userWithRoles);
        setSelectedRoles(userWithRoles.roles?.map(r => r.id) || []);
        setShowRoleModal(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch user roles');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSaveRoles = async () => {
    setError('');
    setSuccess('');

    try {
      const currentRoleIds = selectedUser.roles?.map(r => r.id) || [];
      const rolesToAdd = selectedRoles.filter(id => !currentRoleIds.includes(id));
      const rolesToRemove = currentRoleIds.filter(id => !selectedRoles.includes(id));

      // Add new roles
      for (const roleId of rolesToAdd) {
        await userService.assignRoleToUser(selectedUser.id, roleId);
      }

      // Remove old roles
      for (const roleId of rolesToRemove) {
        await userService.removeRoleFromUser(selectedUser.id, roleId);
      }

      setSuccess('Roles updated successfully!');
      setShowRoleModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Update roles error:', err);
      setError(err.message || 'Failed to update roles');
    }
  };

  const handleOpenWaSubs = async (userRow) => {
    setWaSubsUser(userRow);
    try {
      const res = await userService.getWhatsappSubscriptions(userRow.id);
      const loaded = (res.data.subscriptions || []).map(s => s.template_name);
      const rows = [...loaded];
      while (rows.length < 5) rows.push('');
      setWaSubsRows(rows);
    } catch {
      setWaSubsRows(['', '', '', '', '']);
    }
    setShowWaSubsModal(true);
  };

  const handleWaSubsChange = (index, value) => {
    setWaSubsRows(prev => prev.map((r, i) => i === index ? value : r));
  };

  const handleWaSubsRemove = (index) => {
    setWaSubsRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleWaSubsAddRow = () => {
    setWaSubsRows(prev => [...prev, '']);
  };

  const handleWaSubsSave = async () => {
    setWaSubsSaving(true);
    try {
      await userService.saveWhatsappSubscriptions(waSubsUser.id, waSubsRows.filter(r => r.trim()));
      setSuccess('WhatsApp subscriptions saved successfully');
      setShowWaSubsModal(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save subscriptions');
    } finally {
      setWaSubsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      status: 'A'
    });
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      status: 'A'
    });
    setShowModal(true);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  if (loading && users.length === 0) {
    return (
      <div className="users-management">
        <div className="loading-spinner">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="users-management">
      <div className="users-header">
        <div className="header-content">
          <h1>User Management</h1>
          <p>Manage user accounts and access permissions</p>
        </div>
        <button className="btn-add-user" onClick={handleAddUser}>
          <span className="icon">+</span> Add User
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>
          {error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="users-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search users by username, email, or name..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>
      <div className="users-count">
        {totalUsers} user{totalUsers !== 1 ? 's' : ''} found
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {searchTerm ? 'No users found matching your search.' : 'No users available.'}
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {(user.username || 'U').charAt(0).toUpperCase()}
                      </div>
                      <span>{user.username}</span>
                      {user.id === currentUser?.userId && (
                        <span className="current-user-badge">You</span>
                      )}
                    </div>
                  </td>
                  <td>{user.name || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>{user.phone || '-'}</td>
                  <td>
                    <span className={`status-badge status-${user.status === 'A' ? 'active' : 'inactive'}`}>
                      {user.status === 'A' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{fmtDate(user.created_date)}</td>
                  <td>{fmtDate(user.updated_date)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(user)}
                        title="Edit user"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleManageRoles(user)}
                        title="Manage roles"
                      >
                        🔒
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleOpenWaSubs(user)}
                        title="WhatsApp subscriptions"
                      >
                        💬
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(user.id)}
                        disabled={user.id === currentUser?.userId}
                        title={user.id === currentUser?.userId ? 'Cannot delete yourself' : 'Delete user'}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            ← Previous
          </button>
          <div className="pagination-numbers">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first, last, current, and adjacent pages
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                    disabled={loading}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
          </div>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  className="form-input"
                  minLength={4}
                />
              </div>
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  <option value="A">Active</option>
                  <option value="I">Inactive</option>
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Roles Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content permissions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Manage Roles - {selectedUser.username}</h3>
              <button className="modal-close" onClick={() => setShowRoleModal(false)}>×</button>
            </div>
            <div className="modal-search">
              <input
                type="text"
                placeholder="Search roles..."
                value={roleSearchTerm}
                onChange={(e) => setRoleSearchTerm(e.target.value)}
              />
            </div>
            <div className="permissions-selection">
              {roles.length === 0 ? (
                <p className="no-data">No roles available</p>
              ) : (
                roles
                  .filter(role =>
                    role.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
                    role.description?.toLowerCase().includes(roleSearchTerm.toLowerCase())
                  )
                  .map(role => (
                  <div key={role.id} className="permission-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                      />
                      <div className="permission-info">
                        <strong>{role.name}</strong>
                        <small>{role.description || 'No description'}</small>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowRoleModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-submit" onClick={handleSaveRoles}>
                Save Roles
              </button>
            </div>
          </div>
        </div>
      )}
      {/* WhatsApp Subscriptions Modal */}
      {showWaSubsModal && waSubsUser && (
        <div className="modal-overlay" onClick={() => setShowWaSubsModal(false)}>
          <div className="modal-content wa-subs-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>WhatsApp Subscriptions — {waSubsUser.name || waSubsUser.username}</h3>
              <button className="modal-close" onClick={() => setShowWaSubsModal(false)}>×</button>
            </div>
            <div className="wa-subs-body">
              {waSubsRows.length === 0 && (
                <p className="wa-subs-empty">No subscriptions. Add a template below.</p>
              )}
              {waSubsRows.map((row, index) => (
                <div key={index} className="wa-subs-row">
                  <input
                    type="text"
                    className="wa-subs-input"
                    placeholder="Template name"
                    value={row}
                    onChange={e => handleWaSubsChange(index, e.target.value)}
                  />
                  <button
                    className="wa-subs-remove"
                    onClick={() => handleWaSubsRemove(index)}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="wa-subs-add" onClick={handleWaSubsAddRow}>+ Add row</button>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowWaSubsModal(false)}>
                Cancel
              </button>
              <button className="btn-submit" onClick={handleWaSubsSave} disabled={waSubsSaving}>
                {waSubsSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
